import {FormRequest} from '../interfaces/FormRequest';
import {authHeader} from './idToken';

// The export API is a separate service. This used to be the relative path
// '/bq/query', which worked only through CRA's dev proxy -- since the move to
// Vite every query posted to the dashboard's own origin and came back as the
// index page, so no form could ever return a file.
const API_BASE = (import.meta.env.REACT_APP_BQ_API_URL || 'https://api.missouri-titan.com')
    .replace(/\/+$/, '');
const apiURL = `${API_BASE}/bq/query`;

const website = 'titan';
let authToken = '';

export function setAuthToken(token: string): void {
    authToken = token;
}

/** A job document from GET /bq/job/{id}. */
export interface QueryJob {
    /** Present on jobs read back from /bq/jobs. */
    id?: string;
    job_id?: string;
    created_at?: string;
    /** Short table name, e.g. CRASHES_BATCH; absent on jobs run before it was recorded. */
    table?: string;
    time_range?: {column?: string; start?: string; end?: string};
    sql?: string;
    status: 'queued' | 'running' | 'done' | 'failed';
    stage?: string;
    progress_msg?: string;
    /** Single merged export; crash and incident queries return this one. */
    final_csv_url?: string;
    /** HERE queries only: the matching TMC metadata. */
    static_csv_url?: string;
    /** Sharded exports, when the result is too large for one file. */
    csv_urls?: string[];
    /** Bytes written, and whether the CSV holds anything but its header. */
    file_size?: number;
    is_empty?: boolean;
    error?: string;
}

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000;   // BigQuery exports of a year run long

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function submitJob(payload: any): Promise<string> {
    // Exports are charged to whoever runs them, so the API takes the user from
    // a verified token rather than from the payload.
    const res = await fetch(apiURL, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', ...(await authHeader())},
        body: JSON.stringify(payload),
    });
    if (res.status === 401) {
        throw new Error('Your session expired. Sign in again to run a query.');
    }
    if (!res.ok) {
        throw new Error(`Query rejected (${res.status})`);
    }
    const body = await res.json();
    if (!body.job_id) {
        throw new Error('Query accepted but returned no job id');
    }
    return body.job_id;
}

/**
 * Wait for a submitted job to finish.
 *
 * Exports are asynchronous: the POST only queues the work, and the CSV url
 * appears on the job document minutes later. Reading the POST response for a
 * url -- which is what this app did -- always found nothing.
 */
async function pollJob(jobId: string, onProgress?: (msg: string) => void): Promise<QueryJob> {
    const startedAt = Date.now();
    for (;;) {
        const res = await fetch(`${API_BASE}/bq/job/${jobId}`, {headers: await authHeader()});
        if (!res.ok) {
            throw new Error(`Lost track of the query (${res.status})`);
        }
        const job: QueryJob = await res.json();
        if (onProgress) {
            onProgress(job.progress_msg || job.stage || job.status);
        }
        if (job.status === 'done') {
            return job;
        }
        if (job.status === 'failed') {
            throw new Error(job.error || 'The query failed');
        }
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            throw new Error('The query is taking longer than 30 minutes; try a shorter date range');
        }
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    }
}

/**
 * A short-lived signed link to one of a job's files.
 *
 * Export objects are no longer handed out as permanent public urls: the API
 * checks who is asking and signs a link that expires, so a url copied out of a
 * page stops working rather than granting the data to whoever finds it.
 */
export async function signedDownload(jobId: string, file: 'final' | 'static' = 'final'): Promise<string> {
    const res = await fetch(`${API_BASE}/bq/job/${jobId}/download?file=${file}`,
                            {headers: await authHeader()});
    if (res.status === 410) {
        throw new Error('That file has been deleted. Exports are kept for 30 days -- run the query again.');
    }
    if (!res.ok) {
        throw new Error(`Could not prepare that download (${res.status})`);
    }
    return (await res.json()).url;
}

/** Every export this user has requested, newest first. */
export async function listJobs(): Promise<QueryJob[]> {
    const res = await fetch(`${API_BASE}/bq/jobs`, {headers: await authHeader()});
    if (!res.ok) {
        throw new Error(`Could not load your downloads (${res.status})`);
    }
    return (await res.json()).jobs ?? [];
}

/**
 * Delete an export: its CSVs in cloud storage and its record together.
 * The API refuses jobs belonging to someone else, and keeps the record if
 * the files cannot be removed, so a failed delete can be retried.
 */
export async function deleteJob(jobId: string): Promise<void> {
    const res = await fetch(`${API_BASE}/bq/job/${jobId}`, {
        method: 'DELETE',
        headers: await authHeader(),
    });
    if (!res.ok) {
        const detail = await res.json().catch(() => null);
        throw new Error(detail?.detail || `Delete failed (${res.status})`);
    }
}

/** Submit and wait: the whole round trip a form needs. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function runQuery(payload: any, onProgress?: (msg: string) => void): Promise<QueryJob> {
    const jobId = await submitJob(payload);
    const job = await pollJob(jobId, onProgress);
    return {...job, id: job.id ?? jobId};
}

const bigQueryService = {
    // PROBE Query - optionally scoped to counties, which the API resolves to
    // TMCs through the static table. HERE is clustered by tmc, so that filter
    // cuts what the query scans as well as what comes back.
    queryProbeData: (data: FormRequest, onProgress?: (msg: string) => void): Promise<QueryJob> => {
        const counties = (data.counties ?? []).filter(c => c && c !== "ALL");
        const payload = {
            table: "black-velocity-286217.RIDSI_DATABASE.HERE",
            // No roadway_name: that column does not exist on HERE and the
            // query failed on it. Road names come with the static CSV, whose
            // roadwayname joins back on tmc.
            select: ["pub_millis", "tmc", "ss_speed", "segment_id", "ss_id",
                     "ss_free_flow", "ss_length"],
            time_range: {
                column: "pub_millis",
                start: `${data.start} 00:00:00`,
                end: `${data.end} 23:59:59`
            },
            aggv: {
                interval: data.interval || 60,
                methods: ["avg", "avg"],
                columns: ["ss_speed", "ss_free_flow"]
            },
            static: counties.length > 0
                ? {
                    table: "black-velocity-286217.RIDSI_DATABASE.HERE_STATIC",
                    county: counties.map(c => c.toUpperCase()),
                }
                : {table: "black-velocity-286217.RIDSI_DATABASE.HERE_STATIC"},
            user: data.uid,
            filename : 'probe_data.csv',
            website: website
        };

        return runQuery(payload, onProgress);
    },

    // DETECTOR Query - county filter with "All Counties" option
    // If "All Counties" selected, don't add county filter to payload
    queryDetectorData: (data: FormRequest, onProgress?: (msg: string) => void): Promise<QueryJob> => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const filters: any = {
            lane_Status: "OK"
        };

        // Only add county filter if NOT "All Counties"
        if (data.counties && data.counties.length > 0 && !data.counties.includes("ALL")) {
            if (data.counties.length === 1) {
                filters.county = data.counties[0].toUpperCase();
            } else {
                filters.county = data.counties.map(c => c.toUpperCase());
            }
        }

        const payload = {
            table: "black-velocity-286217.RIDSI_DATABASE.DETECTOR_BATCH",
            select: ["pub_millis", "uuid", "lane_Speed", "lane_Volume",
                     "county", "on_street_name", "on_direction", "latitude", "longitude"],
            filters: filters,
            time_range: {
                column: "pub_millis",
                start: `${data.start} 00:00:00`,
                end: `${data.end} 23:59:59`
            },
            aggv: {
                interval: data.interval || 15,
                methods: ["avg", "sum"],
                columns: ["lane_Speed", "lane_Volume"]
            },
            user: data.uid,
            website: website
        };

        return runQuery(payload, onProgress);
    },

    // CRASH Query - county and severity filters
    queryCrashData: (data: FormRequest, onProgress?: (msg: string) => void): Promise<QueryJob> => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const filters: any = {};

        // Add county filter if selected (not "All Counties")
        if (data.counties && data.counties.length > 0 && !data.counties.includes("ALL")) {
            filters.modot_county_nm = data.counties.map(c => c.toUpperCase());
        }

        // Add severity filter if selected
        if (data.severity && data.severity.length > 0) {
            filters.severity = data.severity;
        }

        const payload = {
            table: "black-velocity-286217.RIDSI_DATABASE.CRASHES_BATCH",
            select: ["hp_acc_image_no", "accident_date", "accident_time", "severity",
                     "modot_county_nm", "landed_latitude", "landed_longitude"],
            filters: filters,
            time_range: {
                column: "accident_date",
                // The form collects whole days; without the times the range
                // ends at midnight and the last day's crashes are missing.
                start: `${data.start} 00:00:00`,
                end: `${data.end} 23:59:59`
            },
            user: data.uid,
            website: website
        };

        return runQuery(payload, onProgress);
    }
};

export default bigQueryService;